import type { components } from './generated/schema';

// The client speaks the contract's own shapes (apps/backend/openapi/schema.yaml,
// via `npm run generate-api`); repositories map them to domain types.
type S = components['schemas'];
type AccountProfile = S['AccountProfile'];
type CancelChangeResult = S['CancelChange'];
type Collection = S['Collection'];
type CollectionCreate = S['CollectionCreateRequest'];
type CollectionUpdate = S['PatchedCollectionUpdateRequest'];
type Comment = S['Comment'];
type ErrorCode = S['ErrorCodeEnum'];
type InvitationCreate = S['InvitationCreateRequest'];
type InvitationCreated = S['InvitationCreated'];
type InviteAcceptResult = S['InviteAcceptResult'];
type InvitePreview = S['InvitePreview'];
type Listing = S['Listing'];
type ListingCreate = S['ListingCreateRequest'];
type ListingCursorPage = S['ListingCursorPage'];
type ListingPage = S['PaginatedListingList'];
type PendingChange = S['PendingChange'];
type PendingImage = S['PendingImage'];
type SocialConnection = S['SocialConnection'];

/**
 * The two profile fields that only change once a code proves ownership.
 *
 * Every call below is parameterized by this rather than written twice, which
 * is what makes "verifying an email and verifying a phone are the same
 * experience" (FR-017) a property of the code instead of an intention — the
 * backend configures one algorithm per purpose for the same reason.
 */
export type VerifiedField = 'email' | 'phone';

/**
 * Build the most informative message available from a failed response body.
 *
 * Server errors arrive in three shapes: a top-level `{detail}` (the error
 * envelope, and DRF's default), a top-level `{error}` (older custom views), or
 * field-keyed validation arrays (`{field: ["msg", ...]}`). Top-level keys win;
 * otherwise we flatten every string across the field values. A body with
 * nothing usable falls back to the HTTP status line.
 */
export function extractErrorMessage(
  errorData: unknown,
  status: number,
  statusText: string,
): string {
  const fallback = `HTTP ${status}: ${statusText}`;
  if (errorData == null || typeof errorData !== 'object') {
    return fallback;
  }
  const data = errorData as Record<string, unknown>;

  if (typeof data.detail === 'string' && data.detail) return data.detail;
  if (typeof data.error === 'string' && data.error) return data.error;

  const fieldMessages = Object.values(data)
    .flat()
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (fieldMessages.length) return fieldMessages.join(' ');

  return fallback;
}

/**
 * A failed API call. `code` is the error envelope's machine-readable code
 * (`{code, detail}`), when the server sent one.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ErrorCode | undefined,
    /** The decoded error body, or null when it wasn't JSON. */
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function errorCode(body: unknown): ErrorCode | undefined {
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code: unknown }).code;
    if (typeof code === 'string') return code as ErrorCode;
  }
  return undefined;
}

const answerListeners = new Set<() => void>();

/**
 * Hear about every answer the service gives this page (any status below 500:
 * a 5xx may be a proxy standing in for it). Draft sync uses it to stop
 * waiting out a backoff once the connection is back (021 R5).
 */
export function onServiceAnswer(listener: () => void): () => void {
  answerListeners.add(listener);
  return () => answerListeners.delete(listener);
}

/** Reload the page so Django serves it as the session now is: signed out, or signed in again. */
export function reloadForSession(): void {
  window.location.reload();
}

let sessionEnded: () => void = reloadForSession;

/**
 * Replace what happens when the server no longer knows this page's session —
 * the account was suspended, or signed out elsewhere. By default the page
 * reloads and Django serves it signed out, so a signed-in-only page goes on
 * to sign-in and back. A signed-in page replaces this so the reload waits
 * until no draft is unsynced (`holdSessionEndWhileUnsynced`). Returns the undo.
 */
export function onSessionEnded(handler: () => void): () => void {
  const previous = sessionEnded;
  sessionEnded = handler;
  return () => {
    sessionEnded = previous;
  };
}

/** The browser's current CSRF token: Django rotates it at sign-in, maybe in another tab. */
function csrfCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * API Client for marketplace operations
 * Centralizes all HTTP requests and provides a consistent interface
 */
class MarketplaceApiClient {
  private getCSRFToken(): string {
    return csrfCookie() || window.MARKETPLACE_CONFIG?.csrfToken || '';
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status < 500) answerListeners.forEach((listener) => listener());
    if (response.status === 401 && window.MARKETPLACE_CONFIG?.user?.isAuthenticated) sessionEnded();
    if (!response.ok) {
      // Narrow the try to the parse alone: a throw built from the parsed body
      // must not be swallowed by the catch meant for a non-JSON response.
      let errorData: unknown = null;
      try {
        errorData = await response.json();
      } catch {
        // Non-JSON body — extractErrorMessage falls back to the status line.
      }
      throw new ApiError(
        extractErrorMessage(errorData, response.status, response.statusText),
        response.status,
        errorCode(errorData),
        errorData,
      );
    }

    // Handle empty responses (like DELETE)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  // ── Transport ────────────────────────────────────────────────────────
  // Public so a repository can reach an operation that has no named method
  // below (mostly the contract's stubbed ones, which answer 501 today).

  get<T>(url: string): Promise<T> {
    return fetch(url, { headers: { 'X-CSRFToken': this.getCSRFToken() } }).then((r) =>
      this.handleResponse<T>(r),
    );
  }

  /** `data` omitted: a bodiless POST (resend, leave, revoke, …). */
  send<T>(
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    url: string,
    data?: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    const withBody = data !== undefined;
    return fetch(url, {
      method,
      headers: withBody
        ? { 'Content-Type': 'application/json', 'X-CSRFToken': this.getCSRFToken() }
        : { 'X-CSRFToken': this.getCSRFToken() },
      body: withBody ? JSON.stringify(data) : undefined,
      signal,
    }).then((r) => this.handleResponse<T>(r));
  }

  /**
   * Put a file straight into blob storage at a signed upload URL (message
   * photos). Another origin: no CSRF token and no cookies go with it.
   */
  async putBlob(url: string, file: File): Promise<void> {
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'omit',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });
    if (!response.ok) {
      throw new ApiError(
        `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        undefined,
      );
    }
  }

  // ── Collections ──────────────────────────────────────────────────────

  /** Every collection I can see — mine, members', and followed owners' open ones. Unpaginated. */
  collectionsList(): Promise<Collection[]> {
    return this.get('/api/v1/collections/');
  }

  collectionGet(id: string): Promise<Collection> {
    return this.get(`/api/v1/collections/${encodeURIComponent(id)}/`);
  }

  collectionCreate(data: CollectionCreate): Promise<Collection> {
    return this.send('POST', '/api/v1/collections/', data);
  }

  collectionUpdate(id: string, data: CollectionUpdate): Promise<Collection> {
    return this.send('PATCH', `/api/v1/collections/${encodeURIComponent(id)}/`, data);
  }

  // ── Listings ─────────────────────────────────────────────────────────

  listingGet(id: string): Promise<Listing> {
    return this.get(`/api/v1/listings/${encodeURIComponent(id)}/`);
  }

  /** Exactly one filter: a collection I can see, or my own listings. */
  listingsList(filter: { collection: string } | { author: 'me' }): Promise<ListingPage> {
    const query = new URLSearchParams(filter).toString();
    return this.get(`/api/v1/listings/?${query}`);
  }

  /** Post to a collection I own, or to my default collection when none is given. */
  listingCreate(data: ListingCreate): Promise<Listing> {
    return this.send('POST', '/api/v1/listings/', data);
  }

  /** Newest first: published listings in collections I can see, minus my own and muted ones. */
  feed(): Promise<ListingCursorPage> {
    return this.get('/api/v1/feed/');
  }

  /** Readable comments, replies nested one level; empty while the collection has them off. */
  commentsList(listingId: string): Promise<Comment[]> {
    return this.get(`/api/v1/listings/${encodeURIComponent(listingId)}/comments/`);
  }

  // ── Pending images (uploaded before the listing exists) ──────────────

  /**
   * No `Content-Type`: the browser sets the multipart boundary itself. With
   * an `id` my device chose, a repeat answers the image already stored.
   */
  async pendingImageUpload(
    file: File,
    { id, signal }: { id?: string; signal?: AbortSignal } = {},
  ): Promise<PendingImage> {
    const body = new FormData();
    body.append('image', file);
    if (id) body.append('id', id);
    const response = await fetch('/api/v1/pending-images/', {
      method: 'POST',
      headers: { 'X-CSRFToken': this.getCSRFToken() },
      body,
      signal,
    });
    return this.handleResponse<PendingImage>(response);
  }

  async pendingImageDelete(id: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(`/api/v1/pending-images/${encodeURIComponent(id)}/`, {
      method: 'DELETE',
      headers: { 'X-CSRFToken': this.getCSRFToken() },
      signal,
    });
    await this.handleResponse<void>(response);
  }

  // ── Invitations ──────────────────────────────────────────────────────

  invitationCreate(data: InvitationCreate): Promise<InvitationCreated> {
    return this.send('POST', '/api/v1/invitations/', data);
  }

  /** Tokens travel in the body, never the path. Works signed out, with a narrower inviter. */
  invitePreview(token: string): Promise<InvitePreview> {
    return this.send('POST', '/api/v1/invites/preview/', { token });
  }

  inviteAccept(token: string, joinedOfferIds: string[]): Promise<InviteAcceptResult> {
    return this.send('POST', '/api/v1/invites/accept/', {
      token,
      joined_offer_ids: joinedOfferIds,
    });
  }

  // ── Account management (feature 007) ─────────────────────────────────

  async accountProfile(): Promise<AccountProfile> {
    const response = await fetch('/api/v1/account/', {
      headers: { 'X-CSRFToken': this.getCSRFToken() },
    });
    return this.handleResponse<AccountProfile>(response);
  }

  async setPassword(data: {
    current_password?: string;
    new_password: string;
    confirm_new_password: string;
  }): Promise<{ detail: string }> {
    const response = await fetch('/api/v1/account/password/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': this.getCSRFToken() },
      body: JSON.stringify(data),
    });
    return this.handleResponse<{ detail: string }>(response);
  }

  async socialConnections(): Promise<SocialConnection[]> {
    const response = await fetch('/api/v1/account/social-connections/', {
      headers: { 'X-CSRFToken': this.getCSRFToken() },
    });
    return this.handleResponse<SocialConnection[]>(response);
  }

  // ── Account profile fields (feature 011) ─────────────────────────────

  /** Save the name. Only `first_name`/`last_name` are writable here. */
  async updateAccountProfile(data: {
    first_name?: string;
    last_name?: string;
  }): Promise<AccountProfile> {
    const response = await fetch('/api/v1/account/', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': this.getCSRFToken() },
      body: JSON.stringify(data),
    });
    return this.handleResponse<AccountProfile>(response);
  }

  /**
   * Upload an avatar, replacing any existing one in the same call.
   *
   * No `Content-Type` header: the browser has to set it itself so the
   * multipart boundary matches the body it generated.
   */
  async uploadAvatar(file: File): Promise<{ avatar_url: string | null }> {
    const body = new FormData();
    body.append('avatar', file);
    const response = await fetch('/api/v1/account/avatar/', {
      method: 'POST',
      headers: { 'X-CSRFToken': this.getCSRFToken() },
      body,
    });
    return this.handleResponse<{ avatar_url: string | null }>(response);
  }

  async deleteAvatar(): Promise<void> {
    const response = await fetch('/api/v1/account/avatar/', {
      method: 'DELETE',
      headers: { 'X-CSRFToken': this.getCSRFToken() },
    });
    await this.handleResponse<void>(response);
  }

  /** Start (or replace) a pending change; the value still needs a code. */
  async requestContactChange(field: VerifiedField, value: string): Promise<PendingChange> {
    const response = await fetch(`/api/v1/account/${field}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': this.getCSRFToken() },
      body: JSON.stringify({ [field]: value }),
    });
    return this.handleResponse<PendingChange>(response);
  }

  /** Submit the code. Success hands back the profile with the change applied. */
  async confirmContactChange(field: VerifiedField, code: string): Promise<AccountProfile> {
    const response = await fetch(`/api/v1/account/${field}/confirm/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': this.getCSRFToken() },
      body: JSON.stringify({ code }),
    });
    return this.handleResponse<AccountProfile>(response);
  }

  /** Issue a fresh code, retiring the previous one and resetting attempts. */
  async resendContactCode(field: VerifiedField): Promise<PendingChange> {
    const response = await fetch(`/api/v1/account/${field}/resend/`, {
      method: 'POST',
      headers: { 'X-CSRFToken': this.getCSRFToken() },
    });
    return this.handleResponse<PendingChange>(response);
  }

  async cancelContactChange(field: VerifiedField): Promise<CancelChangeResult> {
    const response = await fetch(`/api/v1/account/${field}/`, {
      method: 'DELETE',
      headers: { 'X-CSRFToken': this.getCSRFToken() },
    });
    return this.handleResponse<CancelChangeResult>(response);
  }
}

// Export singleton instance
export const apiClient = new MarketplaceApiClient();
export default apiClient;
